import { useState } from "react";
import { Icon } from "swing-components";

import { translate } from "~utils";

import { EditableText } from "../EditableFields";
import { useUpdateSchool } from "./hooks";
import { Footer, ProfileCard } from "./ProfileCard";
import { ErrorList, SchoolArrivingOnsiteFormSchema } from "./SchoolProfileFormUtils";

type DayOfFieldProps = {
  dayOfContact?: string;
  dayOfPhone?: string;
  dayOfEmail?: string;
};

type ParkingFieldProps = {
  parkingCost?: string;
  parkingLocation?: string;
};

type SchoolArrivingOnsiteProps = DayOfFieldProps &
  ParkingFieldProps & {
    schoolId: string;
    checkinLocation?: string;
    checkinProcedure?: string;
    checkoutLocation?: string;
    checkoutProcedure?: string;
  };

export const SchoolArrivingOnsite = ({
  schoolId,
  dayOfContact,
  dayOfPhone,
  dayOfEmail,
  parkingCost,
  parkingLocation,
  checkinLocation,
  checkinProcedure,
  checkoutLocation,
  checkoutProcedure,
}: SchoolArrivingOnsiteProps) => {
  const [editing, setEditing] = useState(false);
  const [formState, setFormState] = useState({
    dayOfContact,
    dayOfPhone,
    dayOfEmail,
    parkingCost,
    parkingLocation,
    checkinLocation,
    checkinProcedure,
    checkoutLocation,
    checkoutProcedure,
  });

  const { errors, updateSchool } = useUpdateSchool({
    formState,
    validationSchema: SchoolArrivingOnsiteFormSchema,
    schoolId,
  });

  const onSave = async () => {
    updateSchool()
      .then(() => setEditing(false))
      .catch();
  };

  return (
    <ProfileCard
      header={translate("ARRIVING_ONSITE_LABEL")}
      isEditing={editing}
      onClick={() => setEditing(true)}
    >
      <div className="flex gap-4 items-center">
        <Icon name="Phone" />
        <h3 className="font-medium">{translate("DAY_OF_CONTACT_HEADER")}</h3>
      </div>

      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        value={formState.dayOfContact}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              dayOfContact: e.detail.value,
            };
          })
        }
        label={translate("DAY_OF_CONTACT_NAME_LABEL")}
        placeholder={translate("DAY_OF_CONTACT_NAME_PLACEHOLDER")}
      />
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        value={formState.dayOfPhone}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              dayOfPhone: e.detail.value,
            };
          })
        }
        label={translate("DAY_OF_CONTACT_PHONE_LABEL")}
        placeholder={translate("DAY_OF_CONTACT_PHONE_PLACEHOLDER")}
      />
      <EditableText
        isInset
        isEditing={editing}
        value={formState.dayOfEmail}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              dayOfEmail: e.detail.value,
            };
          })
        }
        label={translate("DAY_OF_CONTACT_EMAIL_LABEL")}
        placeholder={translate("DAY_OF_CONTACT_EMAIL_PLACEHOLDER")}
      />

      <div className="flex gap-4 items-center">
        <Icon name="Car" />
        <h3 className="font-medium">{translate("PARKING_HEADER")}</h3>
      </div>

      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        value={formState.parkingCost}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              parkingCost: e.detail.value,
            };
          })
        }
        label={translate("PARKING_COST_LABEL")}
        placeholder={translate("PARKING_COST_PLACEHOLDER")}
      />
      <EditableText
        isInset
        isEditing={editing}
        value={formState.parkingLocation}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              parkingLocation: e.detail.value,
            };
          })
        }
        label={translate("PARKING_LOCATION_LABEL")}
        placeholder={translate("PARKING_LOCATION_PLACEHOLDER")}
      />

      <div className="flex gap-4 items-center">
        <Icon name="Enter" />
        <h3 className="font-medium">{translate("CHECK_IN_PROCEDURE_HEADER")}</h3>
      </div>

      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        value={formState.checkinLocation}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              checkinLocation: e.detail.value,
            };
          })
        }
        label={translate("CHECK_IN_LOCATION_LABEL")}
        placeholder={translate("CHECK_IN_LOCATION_PLACEHOLDER")}
      />
      <EditableText
        isInset
        isEditing={editing}
        value={formState.checkinProcedure}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              checkinProcedure: e.detail.value,
            };
          })
        }
        label={translate("CHECK_IN_PROCEDURE_HEADER")}
        placeholder={translate("CHECK_IN_PROCEDURE_PLACEHOLDER")}
      />

      <div className="flex gap-4 items-center">
        <Icon name="Enter" />
        <h3 className="font-medium">{translate("CHECK_OUT_PROCEDURE_HEADER")}</h3>
      </div>

      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        value={formState.checkoutLocation}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              checkoutLocation: e.detail.value,
            };
          })
        }
        label={translate("CHECK_OUT_LOCATION_LABEL")}
        placeholder={translate("CHECK_OUT_LOCATION_PLACEHOLDER")}
      />
      <EditableText
        isInset
        lines="none"
        isEditing={editing}
        value={formState.checkoutProcedure}
        onChange={(e) =>
          setFormState((old) => {
            return {
              ...old,
              checkoutProcedure: e.detail.value,
            };
          })
        }
        label={translate("CHECK_OUT_PROCEDURE_HEADER")}
        placeholder={translate("CHECK_OUT_PROCEDURE_PLACEHOLDER")}
      />
      {editing && (
        <>
          {errors.length > 0 && <ErrorList errorList={errors} />}

          <Footer onSubmit={onSave} onCancel={() => setEditing(false)} />
        </>
      )}
    </ProfileCard>
  );
};
